<template>
	<div class="">
		<div class="flex justify-between mb-4">
			<h2 class="text-base font-medium">
				{{ $t("Color") }}:
				{{ selectedColorLocal?.Name || selectedColorLocal?.Code }}
			</h2>
		</div>
		<RadioGroup
			:id="`${colors?.[0]?.ParametricCode}`"
			v-model="selectedColorLocal"
		>
			<div class="flex flex-wrap items-center gap-4">
				<RadioGroupOption
					v-for="color in colors"
					:id="`${color.ParametricCode}-${color.ParametricId}-${color.Id}`"
					:key="color.Id"
					v-slot="{ checked }"
					as="template"
					:value="color"
				>
					<div
						:class="[
							checked && !shouldShowImage(color)
								? 'ring-2 ring-offset-1 ring-gray-super-dark'
								: '',
							'cursor-pointer',
							'relative flex  items-center justify-center rounded-full p-0.5 focus:outline-none focus:ring-2/focus:ring-offset-2/focus:ring-offset-white focus:ring-gray-super-dark focus:ring-2/focus:ring-offset-2/focus:ring-offset-white '
						]"
					>
						<RadioGroupLabel
							:id="`${color.ParametricCode}-${color.Id}`"
							as="span"
							class="sr-only"
						>
							{{ color.Name || color.Code }}
						</RadioGroupLabel>
						<div
							v-if="shouldShowImage(color)"
							class="p-1 w-20 grid grid-cols-1 gap-2 text-center border-2 relative after:content-[''] after:absolute before:content-[''] before:absolute"
							:class="[
								checked ? 'border-gray-super-dark' : 'border-gray-dark',
								!color?.InStock &&
									'after:top-0 after:left-0 after:w-full after:h-full after:bg-white/50 before:top-0 before:right-0 before:h-[2px] before:w-[175%] before:bg-gray-dark before:rotate-[-55deg] before:origin-top-right'
							]"
						>
							<NuxtImg
								:src="getColorImageUrl(color)"
								:alt="color.Name || color.Code"
								loading="lazy"
								class="w-full h-auto aspect-square object-cover"
							/>
							<span>
								{{ color.Name || color.Code }}
							</span>
						</div>
						<template v-else>
							<!-- A value with an uploaded image (e.g. a "Striped" pattern) shows
								that image in the swatch; plain colours keep the flat circle. -->
							<NuxtImg
								v-if="swatchImages.get(color.Id)"
								:src="swatchImages.get(color.Id)"
								alt=""
								aria-hidden="true"
								loading="lazy"
								width="32"
								height="32"
								class="h-8 w-8 rounded-full border border-gray-darker object-cover"
							/>
							<span
								v-else
								aria-hidden="true"
								:class="['h-8 w-8 rounded-full border border-gray-darker']"
								:style="{
									backgroundColor: `${color.Code ?? color.Description}`
								}"
							/>
							<div
								v-if="!color?.InStock"
								class="w-[88%] h-0.5 bg-black absolute -rotate-45"
							></div>
						</template>
					</div>
				</RadioGroupOption>
			</div>
		</RadioGroup>
	</div>
</template>

<script setup lang="ts">
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from "@headlessui/vue";
import type {
	ParametricListValue,
	ParametricListValuewithCount
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { computed, watch, watchEffect } from "vue";
import { useColorImage } from "~/layers/base/composables/use-color-image";

const { selectedColor } = storeToRefs(useProductStore());
const { shouldShowImage, getColorImageUrl, getColorSwatchImageUrl } =
	useColorImage();

interface Props {
	parametricsColor:
		| (ParametricListValue | ParametricListValuewithCount)[]
		| undefined;
}
const { parametricsColor } = defineProps<Props>();

const colors = ref<
	(ParametricListValue | ParametricListValuewithCount)[] | undefined
>();
const selectedColorLocal = ref<
	ParametricListValue | ParametricListValuewithCount | undefined
>(selectedColor.value ?? undefined);

watchEffect(() => {
	colors.value = parametricsColor;
});

// Value Id -> swatch image URL, so the template resolves each URL once.
const swatchImages = computed(
	() =>
		new Map(
			(colors.value ?? [])
				.filter((color) => !shouldShowImage(color))
				.map((color) => [color.Id, getColorSwatchImageUrl(color)])
		)
);

watch(
	selectedColor,
	(newVal) => {
		if (newVal && newVal.Id !== selectedColorLocal.value?.Id) {
			selectedColorLocal.value = newVal;
		}
	},
	{ immediate: true }
);

watch(selectedColorLocal, () => {
	if (selectedColorLocal.value) {
		useProductStore().addSelectedColor(selectedColorLocal.value);
	}
});
</script>
